import { EventType, type AuthenticationResult } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "@/App";
import { AuthSettingsProvider } from "@/auth/AuthSettingsProvider";
import { DialogProvider } from "@/components/DialogProvider";
import {
  buildLoginRequest,
  createMsalInstance,
  readAuthSettings,
  silentRedirectUri,
} from "@/auth/authConfig";
import { claimSignInAttempt, rememberSignIn, rememberedUser } from "@/auth/session";
import { watchInstallOffer } from "@/install";
import "@/styles.css";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element.");
const root = createRoot(rootElement);

async function bootstrap() {
  // Before anything else: the browser can offer installing the app as soon as it loads.
  watchInstallOffer();
  const settings = readAuthSettings();
  const msalInstance = createMsalInstance(settings);
  await msalInstance.initialize();
  const returned = await msalInstance.handleRedirectPromise();
  if (returned?.account) rememberSignIn(returned.account.username);
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]!);
  }

  // Signed in on this device before and never signed out, but the browser has lost the tokens:
  // go back through Microsoft, which signs them straight in while their session lasts.
  const remembered = rememberedUser();
  if (accounts.length === 0 && remembered && claimSignInAttempt()) {
    await msalInstance.loginRedirect({ ...buildLoginRequest(settings), loginHint: remembered });
    return;
  }

  msalInstance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
      event.payload
    ) {
      const { account } = event.payload as AuthenticationResult;
      if (event.eventType === EventType.LOGIN_SUCCESS) msalInstance.setActiveAccount(account);
      rememberSignIn(account.username);
    }
  });

  // Coming back to the app after a while, renew the token before it is needed, so the first tap
  // doesn't wait on it.
  document.addEventListener("visibilitychange", () => {
    const account = msalInstance.getActiveAccount();
    if (document.visibilityState !== "visible" || !account) return;
    msalInstance
      .acquireTokenSilent({
        scopes: [settings.apiScope],
        account,
        redirectUri: silentRedirectUri(),
      })
      .catch(() => undefined);
  });

  root.render(
    <StrictMode>
      <AuthSettingsProvider settings={settings}>
        <MsalProvider instance={msalInstance}>
          <BrowserRouter>
            <DialogProvider>
              <App />
            </DialogProvider>
          </BrowserRouter>
        </MsalProvider>
      </AuthSettingsProvider>
    </StrictMode>,
  );
}

bootstrap().catch((error: unknown) => {
  root.render(
    <div className="centered">
      <div className="panel">
        <h1>Expenses cannot start</h1>
        <div className="error">{error instanceof Error ? error.message : String(error)}</div>
      </div>
    </div>,
  );
});
